import React, { Fragment, useEffect, useState } from 'react'
import Reflux from 'reflux'
import { toast } from 'react-toastify'
import Showdown from 'showdown'
import Editor from 'rich-markdown-editor'
import light from 'rich-markdown-editor/dist/styles/theme'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Proxies from 'Components/proxies'
const { GET, POST } = Proxies

const customEditorTheme = {
  ...light, // Default theme
  zIndex: 2000 // To make context menus appear in front of the modal backdrop
}

const NotifyUserModal = props => {
  const [orderId, setOrderId] = useState(0)
  const [notifyUserText, setNotifyUserText] = useState('')
  const [notificationType, setNotificationType] = useState('EMAIL')
  const [activeProduct, setActiveProduct] = useState(null)
  const [activeTemplate, setActiveTemplate] = useState(() => blankMessageTemplate)
  const [templateData, setTemplateData] = useState({})
  const [error, setError] = useState('')
  const [order, setOrder] = useState(null)
  const [userData, setUserData] = useState(null)
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [commentText, setCommentText] = useState('')

  const notifyUser = async () => {
    let message = ''
    if (notificationType === 'EMAIL') {
      // Converting markdown to HTML using Showdown
      const converter = new Showdown.Converter({ simpleLineBreaks: true })
      const html = converter.makeHtml(notifyUserText)
      message = html
    } else {
      message = notifyUserText
    }

    try {
      const response = await POST('user/notifyUser', { UserID: props.UserID, OrderID: orderId, message: message, notificationType: notificationType })
      if (response.error) {
        setError('Could not send message to user: ' + response.error.message)
      } else {
        toast.success('Besked blev sendt', {
          autoClose: 3000
        })
        setError(undefined)

        if (commentText) {
          if (commentText === backorderCommentTemplate) {
            const match = notifyUserText.match(/Vi forventer at modtage varen på lager i uge\s+(\d+)/i)
            if (match) {
              const week = match[1]
              setCommentText(commentText.replace('[XXX]', week))
            }
          }
          setShowCommentModal(true)
        }

        props.onHideModal()
      }
    } catch (err) {
      setError('Could not send message to user.')
    }
  }

  const addOrderComment = async () => {
    try {
      const comment = {
        relatedType: 'Order',
        relatedID: orderId,
        message: commentText
      }

      const response = await Proxies.POST('comments/createComment', comment)
      if (response.error) {
        toast.error('Kunne ikke tilføje kommentar: ' + response.error.message, {
          autoClose: 3000
        })
      } else {
        toast.success('Kommentar tilføjet', {
          autoClose: 3000
        })
        setShowCommentModal(false)
      }
    } catch (err) {
      toast.error('Kunne ikke tilføje kommentar: ' + err, {
        autoClose: 3000
      })
    }
  }

  const getUserData = async UserID => {
    try {
      const response = await GET('user/getNotifyUserData', { UserID: UserID })
      if (response.error) {
        setUserData(undefined)
      } else {
        setUserData(response)
      }
    } catch (err) {
      setUserData(undefined)
    }
  }

  const getOrderData = async OrderID => {
    try {
      const response = await GET('orders/getNotifyUserOrderData', { OrderID: OrderID })
      if (response.error) {
        setOrder(undefined)
      } else {
        setOrder(response)
      }
    } catch (err) {
      setOrder(undefined)
    }
  }

  const onNotifyTextChange = v => {
    const formattedText = v()
      .replace(/\n\\/g, '\n') // Editor makes '\' on empty lines, this removes them
      .replace(/\n{2,}/g, match => match.slice(1)) // Editor makes double newlines, this removes 1 of them if there are multiple in a row.
    setNotifyUserText(formattedText)
  }

  const onTemplateClick = templateKey => {
    const messageTemplate = getMessageTemplate(templateKey)
    const commentTemplate = getCommentTemplate(templateKey)
    setActiveTemplate(() => messageTemplate)
    setCommentText(commentTemplate)
    onNotifyTextChange(() => messageTemplate(templateData))
  }

  const getMessageTemplate = templateKey => {
    switch (templateKey) {
      case 'backorder':
        return backorderMessageTemplate
      case 'expired':
        return expiredMessageTemplate
      case 'personalShopper':
        return personalShopperMessageTemplate
      default:
        return blankMessageTemplate
    }
  }

  const getCommentTemplate = templateKey => {
    switch (templateKey) {
      case 'backorder':
        return backorderCommentTemplate
      case 'expired':
        return expiredCommentTemplate
      default:
        return blankCommentTemplate
    }
  }

  const onProductClick = product => {
    setActiveProduct(product)
  }

  const getFirstName = fullName => {
    if (!fullName) {
      return ''
    }

    try {
      return fullName.split(' ')[0]
    } catch (err) {
      return fullName
    }
  }

  const getNotificationTypeLabel = type => {
    if (!type) {
      return '-'
    } else if (type === 'MOBILE') {
      return 'SMS'
    }

    return 'Email'
  }

  const getProductDisplayName = product => {
    if (!product) {
      return undefined
    }

    return `${product.elementTitle?.trim()}${product.variationTitle ? ` (${product.variationTitle})` : ''}`
  }

  useEffect(() => {
    if (props.UserID) {
      getUserData(props.UserID)
    }
  }, [props.UserID])

  useEffect(() => {
    if (props.OrderID) {
      setOrderId(props.OrderID)
      getOrderData(props.OrderID)
    }
  }, [props.OrderID])

  useEffect(() => {
    if (!props.show) {
      setOrder(null)
      setActiveProduct(null)
      setActiveTemplate(() => blankMessageTemplate)
      setError(undefined)
    }
  }, [props.show])

  useEffect(() => {
    const data = {
      userName: getFirstName(userData?.userName),
      brandName: userData?.brandName,
      personalShopperMinimumPrice: userData?.personalShopperMinimumPrice,

      ConfirmationID: order?.ConfirmationID,

      productTitle: getProductDisplayName(activeProduct),
      manufacturer: activeProduct?.manufacturer,
      amount: activeProduct?.amount,

      loggedInUserName: getFirstName(Reflux.GlobalState.LoginStore.settings.userName)
    }
    setTemplateData(data)
    onNotifyTextChange(() => activeTemplate(data))
  }, [activeProduct, userData, order])

  return (
    <Fragment>
      <Modal isOpen={props.show} toggle={() => props.onHideModal()} size='lg'>
        <ModalHeader toggle={() => props.onHideModal()}>{`Send besked til bruger (${userData?.userName})`}</ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Input type='select' value={notificationType} onChange={v => setNotificationType(v.target.value)}>
              <option value={'EMAIL'}>Email</option>
              <option value={'MOBILE'}>SMS</option>
            </Input>
            <div>
              <p>
                <b>Brugers notifikationstype:</b> {getNotificationTypeLabel(userData?.userNotificationType)}
              </p>
              <p>
                <b>Email:</b> {userData?.userEmail || '-'}
              </p>
              <p>
                <b>Telefon:</b> {userData?.userPhone || '-'}
              </p>
            </div>

            <div style={{ marginTop: 20 }}>
              <p>
                <b>Templates</b>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <Button
                  onClick={() => {
                    onTemplateClick('blank')
                  }}
                >
                  Blank
                </Button>
                {!!order && (
                  <Button
                    onClick={() => {
                      onTemplateClick('backorder')
                    }}
                  >
                    Restordre
                  </Button>
                )}
                {!!order && <Button onClick={() => onTemplateClick('expired')}>Udgået</Button>}
                <Button
                  onClick={() => {
                    onTemplateClick('personalShopper')
                  }}
                >
                  Personal shopper
                </Button>
              </div>
            </div>

            {!!order && (
              <div>
                <p>
                  <b>Produkter</b>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {order.products.map((p, i) => (
                    <Button
                      key={i}
                      onClick={() => {
                        onProductClick(p)
                      }}
                      color='info'
                    >
                      {getProductDisplayName(p)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {notificationType === 'EMAIL' ? (
              <Editor
                style={{ border: '1px solid #e4e7ea', borderRadius: '0.25rem', padding: 10, paddingLeft: 30, color: '#5c6873' }}
                onChange={onNotifyTextChange}
                placeholder={''}
                value={activeTemplate(templateData)}
                theme={customEditorTheme}
                disableExtensions={[
                  'checkbox_list',
                  'strikethrough',
                  'checkbox_item',
                  'table',
                  'blockquote',
                  'container_notice',
                  'code_fence',
                  'highlight',
                  'code_block',
                  'code_inline'
                ]}
              />
            ) : (
              <Input type='textarea' value={notifyUserText} onChange={v => setNotifyUserText(v.target.value)} style={{ minHeight: 100 }} />
            )}
            <p style={{ color: 'red' }}>{error}</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => notifyUser(props.UserID, orderId, notifyUserText, notificationType)}
            disabled={notifyUserText?.includes(placeholder)}
          >
            Send
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showCommentModal} toggle={() => setShowCommentModal(false)} size='md' centered>
        <ModalHeader toggle={() => props.onHideModal()}>{`Tilføj kommentar til ordren?`}</ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Input type='textarea' value={commentText} onChange={v => setCommentText(v.target.value)} style={{ minHeight: 100 }} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => addOrderComment()} disabled={commentText?.includes(placeholder)}>
            Kommentér
          </Button>
          <Button onClick={() => setShowCommentModal(false)} color='danger'>
            Luk
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  )
}

export default NotifyUserModal

const placeholder = '[XXX]'

const getTemplateDataValue = value => value || placeholder

const blankMessageTemplate = () => ' '

const backorderMessageTemplate = templateData => `Kære ${getTemplateDataValue(templateData.userName)},

\\

Du har bestilt ${getTemplateDataValue(templateData.amount)} stk. "${getTemplateDataValue(templateData.productTitle)}" fra ${getTemplateDataValue(
  templateData.manufacturer
)} på ordre ${getTemplateDataValue(templateData.ConfirmationID)}.

\\

Varen er desværre i restordre hos vores leverandør.

\\

Vi forventer at modtage varen på lager i uge ${getTemplateDataValue(templateData.week)}, hvorefter denne hurtigst muligt bliver afsendt til dig.

\\

Har du bestilt andre varer, vil disse blive afsendt snarest muligt.

\\

Vi beklager ventetiden mange gange og ønsker dig en god dag.

\\

Hilsen

${getTemplateDataValue(templateData.loggedInUserName)}

${getTemplateDataValue(templateData.brandName)}
`

const expiredMessageTemplate = templateData => `Kære ${getTemplateDataValue(templateData.userName)},

\\

Du har bestilt ${getTemplateDataValue(templateData.amount)} stk. "${getTemplateDataValue(templateData.productTitle)}" fra ${getTemplateDataValue(
  templateData.manufacturer
)} på ordre ${getTemplateDataValue(templateData.ConfirmationID)}.

\\

Varen er udgået hos vores leverandør, hvorfor vi desværre må annullere din ordre. 

\\

Dine point er tilbageført og du er velkommen til at bestille noget andet. 

\\

Vi beklager ulejligheden mange gange!

\\

Du ønskes en god dag. 

\\

Hilsen 

${getTemplateDataValue(templateData.loggedInUserName)}

${getTemplateDataValue(templateData.brandName)}
`

const personalShopperMessageTemplate = templateData => `Hej ${getTemplateDataValue(templateData.userName)},

\\

Tak for din henvendelse. 

\\

Minimumsgrænsen for Personal Shopper Servicen er kr. ${getTemplateDataValue(templateData.personalShopperMinimumPrice)},-.

\\

Vi har derfor ikke mulighed for at imødekomme din forespørgsel.

\\

Jeg beklager og ønsker dig en god dag. 😊
`

const blankCommentTemplate = ''
const backorderCommentTemplate = 'Forventes på lager i uge [XXX]. Brugeren er informeret.'
const expiredCommentTemplate = 'Brugeren er informeret om at produktet er udgået og ordren er refunderet.'
